import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { 
  useAIDisabled, setAIDisabled, 
  useDevMode, setDevMode,
  useColorMode, setColorMode, 
  useCustomColor, setCustomColor,
  useDateFormat, setDateFormat,
  useTimeFormat, setTimeFormat,
  useTimezone, setTimezone,
  useWorkingHours, setWorkingHours
} from "@/utils/settings";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthProvider";
import { toast } from "sonner";

// Define the type for our settings
export type UserSettings = {
  aiDisabled: boolean;
  devMode: boolean;
  colorMode: "light" | "dark" | "custom";
  customColor: string;
  dateFormat: string;
  timeFormat: string;
  timezone: string;
  workingHoursStart: number;
  workingHoursEnd: number;
  calendarSettings?: CalendarSettings;
  sync_init_google?: string; // ISO date string for Google calendar sync start date
  sync_init_microsoft?: string; // ISO date string for Microsoft calendar sync start date
};

// Define the type for our calendar settings
export type CalendarSettings = {
  workDayStart: number;
  workDayEnd: number;
  workWeekDays: string[];
  firstDayOfWeek: number;
  defaultEventDuration: number;
  defaultEventColor: string;
};

// Define the type for our settings context
type SettingsContextType = {
  aiDisabled: boolean;
  toggleAI: (enabled: boolean) => void;
  devMode: boolean;
  toggleDevMode: (enabled: boolean) => void;
  colorMode: "light" | "dark" | "custom";
  setColorModeValue: (mode: "light" | "dark" | "custom") => void;
  customColor: string;
  setCustomColorValue: (color: string) => void;
  resetToDefaultColor: () => void;
  dateFormat: string;
  setDateFormatValue: (format: string) => void;
  timeFormat: string;
  setTimeFormatValue: (format: string) => void;
  timezone: string;
  setTimezoneValue: (timezone: string) => void;
  saveSettings: () => Promise<void>;
  isSaving: boolean;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (value: boolean) => void;
  calendarSettings: CalendarSettings;
  updateCalendarSettings: (settings: CalendarSettings) => void;
};

// Create the context with a default value
const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

// Create a provider component
export const SettingsProvider = ({ children }: { children: ReactNode }) => {
  // Initialize state from localStorage (via utility functions)
  const [aiDisabled, setAiDisabledState] = useState<boolean>(useAIDisabled());
  const [devMode, setDevModeState] = useState<boolean>(useDevMode());
  const [colorMode, setColorModeState] = useState<"light" | "dark" | "custom">(useColorMode() as "light" | "dark" | "custom");
  const [customColor, setCustomColorState] = useState<string>(useCustomColor());
  const [dateFormat, setDateFormatState] = useState<string>(useDateFormat());
  const [timeFormat, setTimeFormatState] = useState<string>(useTimeFormat());
  const [timezone, setTimezoneState] = useState<string>(useTimezone());
  const { startHour: workingHoursStart, endHour: workingHoursEnd } = useWorkingHours();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [calendarSettings, setCalendarSettings] = useState<CalendarSettings>({
    workDayStart: 9,
    workDayEnd: 17,
    workWeekDays: ['1', '2', '3', '4', '5'],
    firstDayOfWeek: 1,
    defaultEventDuration: 60,
    defaultEventColor: "#9b87f5"
  });
  const { user } = useAuth();
  
  const DEFAULT_COLOR = "#0E2356";

  // Effect to load settings from Supabase when user is available
  useEffect(() => {
    if (user) {
      loadUserSettings();
    }
  }, [user]);

  // Load user settings from Supabase
  const loadUserSettings = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (error) {
        console.error("Error loading user settings:", error);
        return;
      }
      
      // Check if data.settings exists before trying to use it
      if (data && data.settings) {
        try {
          const settings = data.settings as UserSettings;
          
          // Update state and localStorage with the loaded settings
          setAiDisabledState(settings.aiDisabled);
          setAIDisabled(settings.aiDisabled);
          
          setDevModeState(settings.devMode);
          setDevMode(settings.devMode);
          
          setColorModeState(settings.colorMode);
          setColorMode(settings.colorMode);
          
          setCustomColorState(settings.customColor);
          setCustomColor(settings.customColor);
          
          setDateFormatState(settings.dateFormat);
          setDateFormat(settings.dateFormat);
          
          setTimeFormatState(settings.timeFormat);
          setTimeFormat(settings.timeFormat);

          setTimezoneState(settings.timezone || 'UTC');
          setTimezone(settings.timezone || 'UTC');

          // Set working hours if they exist
          if (settings.workingHoursStart !== undefined && settings.workingHoursEnd !== undefined) {
            setWorkingHours(settings.workingHoursStart, settings.workingHoursEnd);
          }
          
          // Set calendar settings if they exist
          if (settings.calendarSettings) {
            setCalendarSettings(settings.calendarSettings);
          }
          
          // Apply theme changes with the loaded settings
          applyThemeChanges();
          
          console.log("Settings loaded from profile:", settings);
        } catch (e) {
          console.error("Error parsing settings:", e);
        }
      }
      
      // Reset unsaved changes after loading
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error("Failed to load user settings:", err);
    }
  };

  // Save settings to Supabase
  const saveSettings = async () => {
    if (!user) {
      toast.error("You must be logged in to save settings");
      return;
    }
    
    setIsSaving(true);
    
    try {
      // Get current user settings to preserve sync dates if they exist
      const { data: userProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('settings')
        .eq('id', user.id)
        .single();
      
      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error("Error fetching current settings:", fetchError);
      }
      
      // Prepare settings object
      const settings: UserSettings = {
        aiDisabled,
        devMode,
        colorMode,
        customColor,
        dateFormat,
        timeFormat,
        timezone,
        workingHoursStart,
        workingHoursEnd,
        calendarSettings,
      };
      
      // Preserve existing sync date settings if they exist
      if (userProfile?.settings && typeof userProfile.settings === 'object') {
        const userSettings = userProfile.settings as UserSettings;
        if (userSettings.sync_init_google) {
          settings.sync_init_google = userSettings.sync_init_google;
        }
        if (userSettings.sync_init_microsoft) {
          settings.sync_init_microsoft = userSettings.sync_init_microsoft;
        }
      }
      
      const { error } = await supabase
        .from('profiles')
        .update({ 
          settings: settings 
        })
        .eq('id', user.id);
      
      if (error) {
        throw error;
      }
      
      toast.success("Settings saved successfully");
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error("Error saving settings:", err);
      toast.error("Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  // Effect to apply theme changes when they update
  useEffect(() => {
    applyThemeChanges();
    setHasUnsavedChanges(true);
  }, [colorMode, customColor]);

  // Apply theme changes based on current settings
  const applyThemeChanges = () => {
    // Apply dark mode class if needed
    if (colorMode === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    // Apply custom color if in custom mode
    if (colorMode === 'custom' && customColor) {
      document.documentElement.style.setProperty('--sidebar-background', customColor);
      document.documentElement.style.setProperty('--digger-blue', customColor);
    } else {
      // Reset to default color when not in custom mode
      document.documentElement.style.setProperty('--sidebar-background', DEFAULT_COLOR);
      document.documentElement.style.setProperty('--digger-blue', DEFAULT_COLOR);
    }
  };

  // Toggle AI processing
  const toggleAI = (enabled: boolean) => {
    const isDisabled = !enabled;
    setAiDisabledState(isDisabled);
    setAIDisabled(isDisabled);
    setHasUnsavedChanges(true);
  };

  // Toggle Dev Mode
  const toggleDevMode = (enabled: boolean) => {
    setDevModeState(enabled);
    setDevMode(enabled);
    setHasUnsavedChanges(true);
  };

  // Set color mode
  const setColorModeValue = (mode: "light" | "dark" | "custom") => {
    setColorModeState(mode);
    setColorMode(mode);
    setHasUnsavedChanges(true);
  };

  // Set custom color
  const setCustomColorValue = (color: string) => {
    setCustomColorState(color);
    setCustomColor(color);
    setHasUnsavedChanges(true);
  };

  // Reset to default color
  const resetToDefaultColor = () => {
    setCustomColorValue(DEFAULT_COLOR);
  };
  
  // Set date format
  const setDateFormatValue = (format: string) => {
    setDateFormatState(format);
    setDateFormat(format);
    setHasUnsavedChanges(true);
  };
  
  // Set time format
  const setTimeFormatValue = (format: string) => {
    setTimeFormatState(format);
    setTimeFormat(format);
    setHasUnsavedChanges(true);
  };

  // Set timezone
  const setTimezoneValue = (tz: string) => {
    setTimezoneState(tz);
    setTimezone(tz);
    setHasUnsavedChanges(true);
  };

  // Update calendar settings
  const updateCalendarSettings = (settings: CalendarSettings) => {
    setCalendarSettings(settings);
    setHasUnsavedChanges(true);
  };

  // Create the context value object
  const contextValue: SettingsContextType = {
    aiDisabled,
    toggleAI,
    devMode,
    toggleDevMode,
    colorMode,
    setColorModeValue,
    customColor,
    setCustomColorValue,
    resetToDefaultColor,
    dateFormat,
    setDateFormatValue,
    timeFormat,
    setTimeFormatValue,
    timezone,
    setTimezoneValue,
    saveSettings,
    isSaving,
    hasUnsavedChanges,
    setHasUnsavedChanges, // Expose this to allow components to trigger hasUnsavedChanges
    calendarSettings,
    updateCalendarSettings
  };

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
};

// Custom hook for using the settings context
export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
};
